import { Box, Typography } from '@mui/material';
import { formMaxWidth } from '../constants/general-info-constants';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getBusinessAsUsualForFeedstock,
  postBusinessAsUsualForFeedstock
} from '../../../../../../../actions/rof-burns/01-project-info/01-general-info';
import GeneralInfoForm from '../shared-components/GeneralInfoForm';
import tooltips from '../../../../../../../store/tooltips';

const feedstockSourceTypes = [
  'Hardwood',
  'Softwood',
  'Mixed',
  'Agricultural Waste'
]

const feedstockUses = [
  'It would be burned (pile burn)',
  'It would be sold as firewood',
  'It would be chipped',
  'It would be left to decay',
];

const defaultFormState = {
  feedstockType: '',
  feedstockUse: '',
  hasEvidence: '',
  evidenceUpload: null,
}


export default function BusinessAsUsualForFeedstock({ projectId, selectedCategory }) {
  const dispatch = useDispatch()
  const [formState, setFormState] = useState(defaultFormState)
  const [formErrors, setFormErrors] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [fileInputKey, setFileInputKey] = useState(0)

  const businessAsUsualForFeedstockData = useSelector((state) => (state.generalInfoReducer.businessAsUsualForFeedstock?.data));

  useEffect(() => {
    handleFormErrorReset()
    dispatch(getBusinessAsUsualForFeedstock(projectId))
  }, [selectedCategory, projectId])

  useEffect(() => {
    if (businessAsUsualForFeedstockData)
      setFormState({
        feedstockType: businessAsUsualForFeedstockData?.type,
        feedstockUse: businessAsUsualForFeedstockData?.disposal,
        // handle boolean logic for backend
        hasEvidence: businessAsUsualForFeedstockData?.has_evidence === true ? 'yes' : businessAsUsualForFeedstockData?.has_evidence === false ? 'no' : '',
        evidenceUpload: businessAsUsualForFeedstockData?.evidence_file,
      })
    else {
      setFormState(defaultFormState)
    }

  }, [selectedCategory, projectId, businessAsUsualForFeedstockData])

  const businessAsUsualForFeedstockQuestions = (formState) => {
    const { hasEvidence, feedstockType, feedstockUse } = formState
    const defaultQuestions = [
      {
        type: 'select',
        fieldId: 'feedstockType',
        text: 'Select the primary feedstock type:',
        value: feedstockType,
        options:
          feedstockSourceTypes,
        selectPlaceholder: 'Primary Feedstock',
        tooltip: tooltips.businessAsUsualQ1AndQ2
      },
      {
        type: 'select',
        fieldId: 'feedstockUse',
        text: 'What would normally be done with this feedstock?',
        value: feedstockUse,
        options:
          feedstockUses,
        selectPlaceholder: 'Usual Feedstock Business'
      },
      {
        type: 'radio',
        fieldId: 'hasEvidence',
        text: 'Can you provide evidence of the alternative fate for this feedstock?',
        value: hasEvidence,
      },
    ]

    const optionalQuestions = [
      {
        type: 'file',
        fieldId: 'evidenceUpload',
        text: 'Please upload evidence for the alternative fate of feedstock:',
      },
    ]

    return [
      ...defaultQuestions,
      ...(hasEvidence === 'yes' ? optionalQuestions : [])
    ]
  }

  const onSubmit = async () => {
    try {
      setIsLoading(true)

      const formData = createFormData(formState)

      await dispatch(postBusinessAsUsualForFeedstock(formData, projectId))
      await dispatch(getBusinessAsUsualForFeedstock(projectId))
      setFileInputKey((prevState) => (prevState + 1))
    } catch (error) {
      setFormErrors(error.message)
    } finally {
      setIsLoading(false)
    }
  }

  function createFormData(formState) {
    const formData = new FormData()

    formData.append('feedstockType', formState.feedstockType)
    formData.append('feedstockUse', formState.feedstockUse)
    formData.append('hasEvidence', formState.hasEvidence)

    if (formState.evidenceUpload instanceof File) {
      formData.append('evidenceUpload', formState.evidenceUpload)
    }
    return formData
  }

  const setData = (event) => {
    const { name, type } = event.target;
    let value = event.target.value

    if (type === 'file') {
      value = event.target.files[0]
    }

    setFormState(prevState => ({
      ...prevState,
      [name]: value,
    }));
  }

  const handleFormErrorReset = () => {
    setFormErrors(null);
  };

  const questions = businessAsUsualForFeedstockQuestions(formState)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: formMaxWidth }}>
      <Typography variant='h4' sx={{ pb: 2 }}>
        {selectedCategory}
      </Typography>
      <GeneralInfoForm
        formObjectArray={questions}
        formState={formState}
        formErrors={formErrors}
        isLoading={isLoading}
        fileInputKey={fileInputKey}
        onChange={setData}
        onSubmit={onSubmit} />
    </Box>
  )
}